import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { FormRenderer, FormRendererProps } from "./renderer/form-renderer";
import { datasetFormRequest, inlineFormHosts } from "./dataset-form-signal";

// The resolved renderer spec pushed to open a dataset-form modal (or undefined to
// close). createForm builds this; DatasetFormModal renders it. Mirrors the
// showForm / FormBuilderManager pattern, but drives the NEW FormRenderer.
export type DatasetFormRequest = FormRendererProps;


/** Mount once near the app root (beside FormBuilderManager). Renders the new
 *  FormRenderer in a modal whenever createForm pushes a request. */
export const DatasetFormModal: React.FC = () => {
  // Each request is numbered, and the renderer is keyed by that number: a request that replaces an
  // open form (a picker opening the form it picked, "Show its settings" reopening a channel on another
  // kind) mounts a fresh renderer with its own initial values, instead of the renderer keeping the
  // values of the form it replaced. The same as InlineFormHost.
  const [req, setReq] = useState<{ n: number; props: DatasetFormRequest } | undefined>();
  // A split page's InlineFormHost claims every request while it is mounted.
  const [inlineActive, setInlineActive] = useState(inlineFormHosts.value > 0);
  useEffect(() => {
    let n = 0;
    const sub = datasetFormRequest.subscribe((r) => {
      n += 1;
      setReq(r ? { n, props: r } : undefined);
    });
    const hosts = inlineFormHosts.subscribe((n) => setInlineActive(n > 0));
    return () => {
      sub.unsubscribe();
      hosts.unsubscribe();
    };
  }, []);

  const close = () => datasetFormRequest.next(undefined);

  return (
    <Dialog
      open={!!req && !inlineActive}
      onClose={close}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            background: "transparent",
            boxShadow: "none",
            // Cap the dialog at the viewport (MUI leaves 32px margin top+bottom)
            // and scroll the whole form when it's taller than the window —
            // otherwise tall forms (e.g. Network Pinger) overflow off-screen with
            // no way to reach the top/bottom. Each .dfr form carries its own
            // Save/Cancel pair at the bottom, so submit stays reachable while scrolled.
            maxHeight: "calc(100% - 64px)",
            overflowY: "auto",
          },
        },
      }}
    >
      {req && (
        <div style={{ padding: 4 }}>
          <FormRenderer key={req.n} {...req.props} onCancel={req.props.onCancel ?? close} />
        </div>
      )}
    </Dialog>
  );
};
